import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../_model/api-response';
import { CompleteWithdrawalRequestDto, CreateWithdrawalBeneficiaryRequestDto, FailWithdrawalRequestDto, InitiateWithdrawalRequestDto, InitiateWithdrawalResponseDto, ResolveWithdrawalDestinationRequestDto, ResolveWithdrawalDestinationResponseDto, WithdrawalBeneficiaryDto, WithdrawalDto, WithdrawalFormConfigResponseDto, WithdrawalQuoteRequestDto, WithdrawalQuoteResponseDto, WithdrawalStatusResponseDto } from '../_model/withdrawal';
import { environment } from '../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class WithdrawalService {
  private readonly baseUrl = `${environment.apiBaseUrl}/Withdrawals`;
  private readonly beneficiaryUrl = `${environment.apiBaseUrl}/WithdrawalBeneficiaries`;
  private pageSize = environment.pageSize;

  constructor(private http: HttpClient) {}

  getWithdrawalFormConfig(
    walletId: string,
    paymentMethod?: string | null
  ): Observable<ApiResponse<WithdrawalFormConfigResponseDto>> {
    let params = new HttpParams();

    if (paymentMethod) {
      params = params.set('paymentMethod', paymentMethod);
    }

    return this.http.get<ApiResponse<WithdrawalFormConfigResponseDto>>(
      `${this.baseUrl}/form-config/${walletId}`,
      { params }
    );
  }

  initiateWithdrawal(
    payload: InitiateWithdrawalRequestDto
  ): Observable<ApiResponse<InitiateWithdrawalResponseDto>> {
    return this.http.post<ApiResponse<InitiateWithdrawalResponseDto>>(
      `${this.baseUrl}/initiate`,
      payload
    );
  }

  getUserWithdrawals(
    page = 1,
    walletId?: string | null
  ): Observable<ApiResponse<WithdrawalDto[]>> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', this.pageSize);
    
    return this.http.get<ApiResponse<WithdrawalDto[]>>(
      `${this.baseUrl}/user`,
      { params }
    );
  }

  quoteWithdrawal(
    payload: WithdrawalQuoteRequestDto
  ): Observable<ApiResponse<WithdrawalQuoteResponseDto>> {
    return this.http.post<ApiResponse<WithdrawalQuoteResponseDto>>(
      `${this.baseUrl}/quote`,
      payload
    );
  }

  getWithdrawalById(
    withdrawalId: string
  ): Observable<ApiResponse<WithdrawalDto>> {
    return this.http.get<ApiResponse<WithdrawalDto>>(
      `${this.baseUrl}/${withdrawalId}`
    );
  }

  getWithdrawalStatus(
    externalReference: string
  ): Observable<ApiResponse<WithdrawalStatusResponseDto>> {
    return this.http.get<ApiResponse<WithdrawalStatusResponseDto>>(
      `${this.baseUrl}/status/${externalReference}`
    );
  }

  resolveDestination(
    payload: ResolveWithdrawalDestinationRequestDto
  ): Observable<ApiResponse<ResolveWithdrawalDestinationResponseDto>> {
    return this.http.post<ApiResponse<ResolveWithdrawalDestinationResponseDto>>(
      `${this.baseUrl}/resolve-destination`,
      payload
    );
  }

  retryWithdrawal(
    withdrawalId: string
  ): Observable<ApiResponse<InitiateWithdrawalResponseDto>> {
    return this.http.post<ApiResponse<InitiateWithdrawalResponseDto>>(
      `${this.baseUrl}/${withdrawalId}/retry`,
      {}
    );
  }

  completeWithdrawal(
    withdrawalId: string,
    payload: CompleteWithdrawalRequestDto
  ): Observable<ApiResponse<WithdrawalDto>> {
    return this.http.post<ApiResponse<WithdrawalDto>>(
      `${this.baseUrl}/${withdrawalId}/complete`,
      payload
    );
  }

  failWithdrawal(
    withdrawalId: string,
    payload: FailWithdrawalRequestDto
  ): Observable<ApiResponse<WithdrawalDto>> {
    return this.http.post<ApiResponse<WithdrawalDto>>(
      `${this.baseUrl}/${withdrawalId}/fail`,
      payload
    );
  }

  getBeneficiaries(
    walletId: string
  ): Observable<ApiResponse<WithdrawalBeneficiaryDto[]>> {
    const params = new HttpParams().set('walletId', walletId);

    return this.http.get<ApiResponse<WithdrawalBeneficiaryDto[]>>(
      this.beneficiaryUrl,
      { params }
    );
  }

  createBeneficiary(
    payload: CreateWithdrawalBeneficiaryRequestDto
  ): Observable<ApiResponse<WithdrawalBeneficiaryDto>> {
    return this.http.post<ApiResponse<WithdrawalBeneficiaryDto>>(
      this.beneficiaryUrl,
      payload
    );
  }

  toggleBeneficiaryStatus(
    beneficiaryId: string
  ): Observable<ApiResponse<WithdrawalBeneficiaryDto>> {
    return this.http.put<ApiResponse<WithdrawalBeneficiaryDto>>(
      `${this.beneficiaryUrl}/${beneficiaryId}/toggle-status`,
      {}
    );
  }
}